import { memo, useRef, useState, useEffect } from 'preact/compat';
import { useAppDispatch, useAppSelector } from '../hooks';
import { uploadExcel, parseSheet, parseAllRows, saveMappingCache, cleanupExcel, resetExcel, setSelectedSheet } from '../store/excelSlice';
import { saveOfferRows, setError, fetchOfferRows, fetchOfferColumns } from '../store/offersSlice';
import { selectSelectedOffer } from '../store/selectors';
import LoadingOverlay from './LoadingOverlay';
import SearchableSelect from './SearchableSelect';
import OfferSkeleton from './OfferSkeleton';

const AUTO_SELECT_SHEET = 'предложение';

const SettingsPage = memo(() => {
    const dispatch = useAppDispatch();
    const offer = useAppSelector(selectSelectedOffer);
    const refs = useAppSelector(s => s.references.data);
    const excel = useAppSelector(s => s.excel);
    const rows = useAppSelector(s => s.offers.rows);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const offerColumns = refs?.offer_columns ?? [];

    // Local editable mapping state
    const [localMapping, setLocalMapping] = useState<Record<string, number | ''>>({});

    // Sync local mapping when cached/auto mapping changes
    useEffect(() => {
        const base = excel.cachedMapping ?? excel.autoMapping ?? {};
        const mapped: Record<string, number | ''> = {};
        for (const [k, v] of Object.entries(base)) {
            mapped[k] = v;
        }
        setLocalMapping(mapped);
    }, [excel.cachedMapping, excel.autoMapping]);

    const currentMapping = localMapping;

    if (!offer) return <OfferSkeleton />;

    const handleFileClick = () => fileInputRef.current?.click();

    const handleFileSelect = async (e: Event) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;

        const ext = file.name.toLowerCase();
        if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls')) {
            dispatch(setError('Поддерживаются только файлы .xlsx и .xls'));
            return;
        }

        const result = await dispatch(uploadExcel({ file, offerCode: offer.code })).unwrap();

        if (result.sheet_names.length === 0) {
            dispatch(setError('В файле нет листов'));
            return;
        }

        // Auto-select sheet
        let targetSheet: string | null = null;
        if (result.sheet_names.length === 1) {
            targetSheet = result.sheet_names[0];
        } else {
            targetSheet = result.sheet_names.find(
                s => s.toLowerCase() === AUTO_SELECT_SHEET.toLowerCase()
            ) ?? null;
        }

        if (targetSheet) {
            dispatch(setSelectedSheet(targetSheet));
            await dispatch(parseSheet({
                tmpPath: result.tmp_path,
                sheetName: targetSheet,
                vendorCode: offer.vendor_code ?? undefined,
                offerCode: Number(offer.code) || undefined,
            }));
        }

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSheetSelect = async (sheetName: string) => {
        if (!excel.tmpPath) return;
        dispatch(setSelectedSheet(sheetName));
        await dispatch(parseSheet({
            tmpPath: excel.tmpPath,
            sheetName,
            vendorCode: offer.vendor_code ?? undefined,
        }));
    };

    const handleMappingChange = (headerIndex: string, offerColumnCode: number | null) => {
        setLocalMapping(prev => {
            const next = { ...prev };
            if (offerColumnCode) {
                next[headerIndex] = offerColumnCode;
            } else {
                delete next[headerIndex];
            }
            return next;
        });
    };

    const handleApply = async () => {
        if (!excel.tmpPath || !excel.selectedSheet) return;

        // Build clean mapping (only entries with actual column codes)
        const cleanMapping: Record<string, number> = {};
        for (const [k, v] of Object.entries(currentMapping)) {
            if (v) cleanMapping[k] = Number(v);
        }

        if (Object.keys(cleanMapping).length === 0) {
            dispatch(setError('Сопоставьте хотя бы один столбец'));
            return;
        }

        // Check for duplicate column codes
        const codeCount: Record<number, number> = {};
        for (const code of Object.values(cleanMapping)) {
            codeCount[code] = (codeCount[code] || 0) + 1;
        }
        const dupes = Object.entries(codeCount).filter(([, c]) => c > 1);
        if (dupes.length > 0) {
            const dupeNames = dupes.map(([code]) => {
                const col = offerColumns.find(c => Number(c.code) === Number(code));
                return col?.name || code;
            });
            dispatch(setError(`Дубликаты столбцов: ${dupeNames.join(', ')}. Один столбец предложения может быть сопоставлен только одному столбцу Excel.`));
            return;
        }

        // Save mapping to cache
        await dispatch(saveMappingCache({
            headers: excel.headers,
            mapping: cleanMapping,
            vendorCode: offer.vendor_code ?? undefined,
        }));

        // Build columns array for offer_row_edit
        const columns = Object.entries(cleanMapping).map(([headerIdx, offerColumnCode]) => ({
            offer_column_code: offerColumnCode,
            column_excel_id: String(headerIdx),
            column_excel_name: excel.headers[Number(headerIdx)] || '',
        }));

        // Always parse ALL rows for import (full data + images)
        let allData = excel.previewData;
        let imageMap: Record<string, string> = {};
        let rowIndices: number[] = [];
        if (excel.tmpPath && excel.selectedSheet) {
            try {
                const fullResult = await dispatch(parseAllRows({
                    tmpPath: excel.tmpPath,
                    sheetName: excel.selectedSheet,
                    offerCode: Number(offer.code),
                })).unwrap();
                allData = fullResult.data;
                if (fullResult.image_map) {
                    imageMap = fullResult.image_map;
                }
                if (fullResult.row_indices) {
                    rowIndices = fullResult.row_indices;
                }
            } catch {
                // Fallback to preview data if full parse fails
            }
        }

        const offerRows = allData.map((row, idx) => {
            const rowObj: Record<string, any> = {
                num_row: idx + 1,
                offer_code: Number(offer.code),
            };

            // Map each Excel column to the row fields based on offer_column mapping
            Object.entries(cleanMapping).forEach(([headerIdx, offerColumnCode]) => {
                const colIdx = Number(headerIdx);
                const offerCol = offerColumns.find(c => Number(c.code) === Number(offerColumnCode));
                if (offerCol?.name_field) {
                    rowObj[offerCol.name_field] = row[colIdx] ?? null;
                }
            });

            // Attach image URL if available
            // row_indices maps data idx → original 0-based Excel row (for image anchor matching)
            const excelRow = rowIndices[idx] ?? (idx + (excel.headerRow ?? 0) + 1);
            if (imageMap[String(excelRow)]) {
                rowObj.foto_url = imageMap[String(excelRow)];
            }

            return rowObj;
        });

        // Save to Oracle
        await dispatch(saveOfferRows({
            code: Number(offer.code),
            columns,
            rows: offerRows,
        }));

        // Reload rows and columns for other tabs
        dispatch(fetchOfferRows({ code: Number(offer.code) }));
        dispatch(fetchOfferColumns(Number(offer.code)));

        // Cleanup
        if (excel.tmpPath) {
            await dispatch(cleanupExcel(excel.tmpPath));
        }
        dispatch(resetExcel());
    };

    const handleCancel = async () => {
        if (excel.tmpPath) {
            await dispatch(cleanupExcel(excel.tmpPath));
        }
        dispatch(resetExcel());
    };

    const loadingRows = useAppSelector(s => s.offers.loadingRows);
    const isLoading = excel.uploading || excel.parsing || excel.importing || loadingRows;

    const hasLoadedRows = rows.length > 0;

    // Step: already loaded — show status, no re-upload
    const showAlreadyLoaded = hasLoadedRows && !excel.tmpPath;
    // Step: no file uploaded yet (and no rows loaded)
    const showUpload = !excel.tmpPath && !hasLoadedRows;
    // Step: file uploaded, multiple sheets, none selected
    const showSheetPicker = excel.tmpPath && excel.sheetNames.length > 1 && excel.headers.length === 0;
    // Step: headers parsed, show mapping
    const showMapping = excel.headers.length > 0;

    return (
        <div className="h-full flex flex-col relative">
            <LoadingOverlay visible={isLoading} text={
                excel.uploading ? 'Загрузка файла...' :
                excel.parsing ? 'Парсинг данных...' :
                excel.importing ? 'Подготовка строк...' :
                loadingRows ? 'Сохранение в Oracle...' : 'Загрузка...'
            } />

            <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center min-h-0">

                {showAlreadyLoaded && (
                    <div className="max-w-xl w-full text-center">
                        <svg className="w-16 h-16 mx-auto text-success mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="text-lg font-semibold mb-2">Данные загружены</h2>
                        <p className="text-base-content/60 mb-2">{rows.length} строк в предложении</p>
                        <p className="text-sm text-base-content/40 mb-4">Перейдите во вкладку "Обработка" для работы со строками</p>
                        <div className="flex gap-2 justify-center">
                            <button className="btn btn-sm btn-outline" onClick={handleFileClick}>
                                Загрузить новый файл
                            </button>
                            <button className="btn btn-sm btn-ghost border border-base-300" onClick={() => {
                                const token = document.cookie.match(/b24_token=([^;]+)/)?.[1] || '';
                                window.open(`/api/offers/${Number(offer.code)}/export?_token=${encodeURIComponent(token)}`, '_blank');
                            }}>
                                Экспорт Excel
                            </button>
                        </div>
                        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileSelect} />
                    </div>
                )}

                {showUpload && (
                    <div className="max-w-xl w-full">
                        <h2 className="text-lg font-semibold mb-4 text-center">Загрузка данных</h2>
                        <div
                            className="border-2 border-dashed border-base-300 rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
                            onClick={handleFileClick}
                        >
                            <svg className="w-12 h-12 mx-auto text-base-content/40 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="text-base-content/70 mb-2">Нажмите для выбора файла</p>
                            <p className="text-sm text-base-content/50">Поддерживаются .xlsx и .xls</p>
                        </div>
                        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileSelect} />
                    </div>
                )}

                {showSheetPicker && (
                    <div className="max-w-md w-full">
                        <h2 className="text-lg font-semibold mb-4 text-center">Выберите лист</h2>
                        <p className="text-sm text-base-content/60 mb-4 text-center">Файл: {excel.fileName}</p>
                        <div className="space-y-2">
                            {excel.sheetNames.map(name => (
                                <button
                                    key={name}
                                    className="btn btn-outline w-full justify-start"
                                    onClick={() => handleSheetSelect(name)}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                        <div className="mt-4 text-center">
                            <button className="btn btn-ghost btn-sm" onClick={handleCancel}>Отмена</button>
                        </div>
                    </div>
                )}

                {showMapping && (
                    <div className="max-w-4xl w-full flex flex-col">
                        <div className="flex-shrink-0">
                            <h2 className="text-lg font-semibold mb-2 text-center">Сопоставление столбцов</h2>
                            <p className="text-base-content/70 mb-2 text-center">
                                Файл: {excel.fileName} / {excel.selectedSheet}
                            </p>
                            <p className="text-sm text-base-content/50 mb-2 text-center">
                                {excel.totalDataRows} строк данных
                                {excel.cachedMapping && <span className="badge badge-accent badge-sm ml-2">Маппинг из кэша</span>}
                            </p>
                            <p className="text-sm mb-4 text-center">
                                {(() => {
                                    const usedColumnCodes = new Set(
                                        Object.values(currentMapping).filter(Boolean).map(v => Number(v))
                                    );
                                    const matched = offerColumns.filter(c => usedColumnCodes.has(Number(c.code))).length;
                                    const total = offerColumns.length;
                                    const color = matched === total ? 'text-success' : matched > 0 ? 'text-warning' : 'text-error';
                                    return <span className={color}>Сопоставлено столбцов: {matched} / {total}</span>;
                                })()}
                            </p>
                        </div>

                        <div className="space-y-1.5 mb-3">
                            {(() => {
                                const usedCodes: Record<number, number> = {};
                                for (const v of Object.values(currentMapping)) {
                                    if (v) {
                                        const n = Number(v);
                                        usedCodes[n] = (usedCodes[n] || 0) + 1;
                                    }
                                }
                                const duplicateCodes = new Set(Object.entries(usedCodes).filter(([, c]) => c > 1).map(([k]) => Number(k)));

                                return excel.headers.map((header, index) => {
                                    const mappedCode = currentMapping[String(index)] ?? '';
                                    const isDuplicate = mappedCode && duplicateCodes.has(Number(mappedCode));

                                    return (
                                        <div key={index} className={`flex items-center gap-2 px-3 py-1.5 rounded ${isDuplicate ? 'bg-error/10 border border-error/30' : 'bg-base-200'}`}>
                                            <div className="flex-1 min-w-0 text-sm truncate" title={header}>{header}</div>
                                            <svg className="w-4 h-4 text-base-content/30 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                            </svg>
                                            <div className="flex-1 min-w-0">
                                                <SearchableSelect
                                                    options={offerColumns.map(col => ({ value: col.code, label: col.name }))}
                                                    value={mappedCode}
                                                    onChange={(v) => handleMappingChange(String(index), Number(v) || null)}
                                                    placeholder="—"
                                                />
                                                {isDuplicate && <span className="text-error text-[10px]">дубликат</span>}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>

                        {/* Preview table */}
                        {excel.previewData.length > 0 && (
                            <div className="flex-shrink-0 mb-3 overflow-x-auto border border-base-300 rounded">
                                <table className="table table-xs">
                                    <thead>
                                        <tr>
                                            {excel.headers.map((h, i) => (
                                                <th key={i} className="text-xs truncate max-w-[120px]">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {excel.previewData.slice(0, 5).map((row, ri) => {
                                            const excelRow = excel.rowIndices?.[ri] ?? (ri + (excel.headerRow ?? 0) + 1);
                                            const imgUrl = excel.imageMap?.[String(excelRow)];
                                            return (
                                                <tr key={ri}>
                                                    {row.map((cell: any, ci: number) => (
                                                        <td key={ci} className="text-xs truncate max-w-[120px]">
                                                            {cell ?? ''}
                                                            {ci === 0 && imgUrl && (
                                                                <a href={imgUrl} target="_blank" rel="noreferrer" className="ml-1 text-accent underline text-[10px]">img</a>
                                                            )}
                                                        </td>
                                                    ))}
                                                    {imgUrl && (
                                                        <td className="text-xs">
                                                            <img src={imgUrl} alt="" className="h-6 w-6 object-cover rounded" />
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="flex-shrink-0 flex gap-2 justify-center py-4 border-t border-base-300 bg-base-100">
                            <button className="btn btn-ghost" onClick={handleCancel}>Отмена</button>
                            <button className="btn btn-accent" onClick={handleApply}>Применить</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default SettingsPage;
